/**
 * Modul "Kalender": gemeinsamer Clubkalender mit vollständiger Monatsansicht.
 * Termine werden ausschließlich für den aktuell angezeigten Monat geladen
 * (siehe GET /api/calendar/events?year=&month=) - beim Wechsel des Monats
 * wird nur dieser Ausschnitt per API nachgeladen, kein Neuladen der gesamten
 * Seite. Ohne "calendar.manage" ist die Ansicht rein lesend (kein Erstellen/
 * Bearbeiten/Löschen möglich) - serverseitig ohnehin über requirePermission()
 * in backend/src/routes/calendar.ts erzwungen, das Ausblenden hier ist nur UX.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { EventForm } from "./EventForm";
import type { CalendarEvent } from "../../types";

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

interface GridCell {
  date: string;
  day: number;
  inMonth: boolean;
}

/** Baut eine vollständige Wochenraster-Ansicht (Montag-Start) für den angegebenen Monat, inkl. Rand-Tagen der Nachbarmonate zur Auffüllung. */
function buildMonthGrid(year: number, month: number): GridCell[] {
  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const firstWeekday = (firstOfMonth.getUTCDay() + 6) % 7; // Montag = 0
  const totalCells = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;

  const cells: GridCell[] = [];
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(Date.UTC(year, month - 1, i - firstWeekday + 1));
    cells.push({ date: d.toISOString().slice(0, 10), day: d.getUTCDate(), inMonth: d.getUTCMonth() === month - 1 });
  }
  return cells;
}

export default function Calendar() {
  const canManage = usePermission("calendar.manage");
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [formState, setFormState] = useState<{ event: CalendarEvent | null; defaultDate?: string } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    return api
      .get<CalendarEvent[]>(`/api/calendar/events?year=${year}&month=${month}`)
      .then(setEvents)
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  function goToPrevMonth() {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); } else { setMonth((m) => m - 1); }
  }
  function goToNextMonth() {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); } else { setMonth((m) => m + 1); }
  }
  function goToToday() {
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  }

  const cells = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const e of events) (map[e.event_date] ??= []).push(e);
    return map;
  }, [events]);

  const monthLabel = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("de-DE", { month: "long", year: "numeric", timeZone: "UTC" });
  const todayStr = today.toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={goToPrevMonth}
            aria-label="Vorheriger Monat"
            className="w-9 h-9 flex items-center justify-center rounded border border-surface-border bg-surface-raised text-gray-300 hover:text-accent hover:border-accent transition-colors"
          >
            ←
          </button>
          <button
            type="button"
            onClick={goToNextMonth}
            aria-label="Nächster Monat"
            className="w-9 h-9 flex items-center justify-center rounded border border-surface-border bg-surface-raised text-gray-300 hover:text-accent hover:border-accent transition-colors"
          >
            →
          </button>
          <h1 className="text-xl font-semibold text-gray-100 capitalize ml-1">{monthLabel}</h1>
          <button type="button" onClick={goToToday} className="text-xs text-accent hover:underline ml-1">
            Heute
          </button>
        </div>
        {canManage && (
          <button className="btn-primary" onClick={() => setFormState({ event: null, defaultDate: todayStr })}>
            + Neuer Termin
          </button>
        )}
      </div>

      <Card padded={false} className="overflow-hidden">
        <div className="grid grid-cols-7 border-b border-surface-border bg-black/20">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="px-2 py-2 text-center text-xs font-display font-semibold uppercase tracking-widest text-sand-muted">
              {w}
            </div>
          ))}
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <div className="grid grid-cols-7">
            {cells.map((cell) => {
              const dayEvents = eventsByDate[cell.date] ?? [];
              const isToday = cell.date === todayStr;
              const clickable = cell.inMonth && canManage;
              return (
                <div
                  key={cell.date}
                  onClick={() => clickable && setFormState({ event: null, defaultDate: cell.date })}
                  className={`min-h-[104px] border-b border-r border-surface-border/60 p-1.5 flex flex-col gap-1 transition-colors ${
                    cell.inMonth ? "bg-transparent" : "bg-black/10"
                  } ${clickable ? "cursor-pointer hover:bg-surface-raised/50" : ""}`}
                >
                  <div className="flex items-center justify-between shrink-0">
                    <span
                      className={`text-xs font-display font-semibold w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday ? "bg-accent text-surface" : cell.inMonth ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {cell.day}
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto space-y-1 max-h-[76px]">
                    {dayEvents.map((ev) => (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormState({ event: ev });
                        }}
                        className="w-full text-left px-1.5 py-0.5 rounded text-[11px] leading-tight text-gray-100 truncate hover:brightness-125 transition-[filter]"
                        style={{ backgroundColor: `${ev.color}33`, borderLeft: `3px solid ${ev.color}` }}
                        title={`${ev.title} — ${ev.start_time}${ev.end_time ? ` – ${ev.end_time}` : ""} Uhr`}
                      >
                        <span className="font-medium">{ev.start_time}</span> {ev.title}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {formState !== null && (
        <EventForm
          event={formState.event}
          defaultDate={formState.defaultDate}
          readOnly={!canManage && !!formState.event}
          onClose={() => setFormState(null)}
          onSaved={() => { setFormState(null); load(); }}
        />
      )}
    </div>
  );
}
